import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
  pdf,
} from "@react-pdf/renderer";
import { format } from "date-fns";
import { ProjectWithRelations } from "@/hooks/useProjects";
import {
  ProjectAvailability,
  ProjectFunctionGroup,
  ProjectFunctionSeries,
  ProjectPersonTravelDay,
} from "@/hooks/useProjectPeople";
import type { IntercomAssignment } from "@/hooks/useProjectIntercom";
import { MealPlan, HotelPlan } from "@/hooks/useProjectPlanning";
import logoImage from "@/assets/logo-livemotion.png";
import { formatProjectName } from "@/lib/format-project-name";
import { getMainFunctionGroupForPerson } from "@/lib/people-planning-groups";

Font.register({
  family: "Open Sans",
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/open-sans@latest/latin-400-normal.ttf",
      fontWeight: 400,
    },
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/open-sans@latest/latin-600-normal.ttf",
      fontWeight: 600,
    },
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/open-sans@latest/latin-700-normal.ttf",
      fontWeight: 700,
    },
  ],
});

const s = StyleSheet.create({
  page: {
    fontFamily: "Open Sans",
    fontSize: 8,
    paddingBottom: 50,
    paddingHorizontal: 30,
    paddingTop: 30,
  },
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 15,
  },
  logo: {
    width: 124,
    height: 28,
  },
  headerRight: {
    alignItems: "flex-end",
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
  },
  subtitle: {
    fontSize: 9,
    color: "#666666",
    marginTop: 2,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginTop: 14,
    marginBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    paddingBottom: 3,
  },
  remarksBlock: {
    marginTop: 6,
    marginBottom: 4,
    padding: 6,
    backgroundColor: "#f9fafb",
    borderLeftWidth: 2,
    borderLeftColor: "#6366f1",
  },
  remarksLabel: {
    fontSize: 7,
    fontWeight: 700,
    marginBottom: 2,
    color: "#4b5563",
  },
  remarksText: {
    fontSize: 7,
    color: "#374151",
  },
  intercomHero: {
    marginTop: 6,
    marginBottom: 14,
    padding: 12,
    backgroundColor: "#111827",
    color: "#ffffff",
  },
  intercomLabel: {
    fontSize: 7,
    fontWeight: 700,
    color: "#c7d2fe",
    marginBottom: 4,
    textTransform: "uppercase",
  },
  intercomHostname: {
    fontSize: 18,
    fontWeight: 700,
  },
  intercomManual: {
    fontSize: 8,
    color: "#e5e7eb",
    marginTop: 6,
  },
  credentialName: { width: "38%" },
  credentialUsername: { width: "31%" },
  credentialPassword: { width: "31%" },
  table: {
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#e5e7eb",
    paddingVertical: 3,
    paddingHorizontal: 4,
    minHeight: 18,
  },
  tableRowAlt: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#e5e7eb",
    paddingVertical: 3,
    paddingHorizontal: 4,
    minHeight: 18,
    backgroundColor: "#f9fafb",
  },
  headerText: {
    fontSize: 7,
    fontWeight: 700,
  },
  cellText: {
    fontSize: 7,
  },
  cellTextSmall: {
    fontSize: 6,
    color: "#666666",
  },
  matrixCell: {
    flex: 1,
    paddingHorizontal: 1,
  },
  matrixText: {
    fontSize: 5.5,
    textAlign: "center",
  },
  matrixRemarkText: {
    fontSize: 5,
    color: "#666666",
    textAlign: "center",
  },
  colPlanName: { width: "22%" },
  colDay: { flex: 1, textAlign: "center" },
  roomType: { width: "14%" },
  roomPeople: { width: "28%" },
  roomNotes: { width: "16%" },
  roomSummaryLabel: { width: "58%" },
  roomDay: { flex: 1, textAlign: "center" },
  summaryRow: {
    flexDirection: "row",
    paddingVertical: 3,
    paddingHorizontal: 4,
    backgroundColor: "#e0e7ff",
    fontWeight: 600,
  },
  groupHeaderRow: {
    paddingVertical: 3,
    paddingHorizontal: 4,
    backgroundColor: "#e5e7eb",
  },
  groupHeaderText: {
    fontSize: 7,
    fontWeight: 700,
    color: "#374151",
  },
  allergySummaryText: {
    fontSize: 7,
    color: "#78350f",
  },
  footer: {
    position: "absolute",
    bottom: 15,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    fontSize: 6,
    color: "#999999",
  },
});

export type LocationFilter = "all" | "remote" | "on_site";

export interface CrewListPDFOptions {
  includeContactData: boolean;
  includeIntercomData: boolean;
  locationFilter: LocationFilter;
}

interface CrewListPDFData {
  project: ProjectWithRelations;
  availability: ProjectAvailability[];
  functions: ProjectFunctionSeries[];
  functionGroups: ProjectFunctionGroup[];
  mealPlans: MealPlan[];
  hotelPlans: HotelPlan[];
  intercomAssignments: IntercomAssignment[];
  intercomHostname?: string | null;
  travelDays?: ProjectPersonTravelDay[];
  cameraNumberByAssignmentId?: Record<string, string>;
  projectDays: Date[];
  eventDays: Date[];
  options: CrewListPDFOptions;
}

const formatTime = (time: string | null) => {
  if (!time) return "";
  return time.slice(0, 5);
};

const CrewListDocument = ({ data }: { data: CrewListPDFData }) => {
  const {
    project,
    availability,
    functions,
    functionGroups,
    mealPlans,
    hotelPlans,
    intercomAssignments,
    intercomHostname,
    travelDays = [],
    cameraNumberByAssignmentId = {},
    projectDays,
    eventDays,
    options,
  } = data;

  const allActivePeople = availability.filter(
    (a) => a.status === "available" || a.status === "booked",
  );

  // Filter by location type if specified
  const activePeople =
    options.locationFilter === "all"
      ? allActivePeople
      : allActivePeople.filter((a) => {
          return functions.some(
            (series) =>
              (series.location_type ??
                series.project_function_group?.location_type) ===
                options.locationFilter &&
              series.days.some((day) =>
                day.assignments?.some((ass) => ass.person_id === a.person_id),
              ),
          );
        });

  const mealMap = new Map<string, MealPlan>();
  mealPlans.forEach((mp) => mealMap.set(`${mp.person_id}_${mp.plan_date}`, mp));

  const hotelMap = new Map<string, HotelPlan>();
  hotelPlans.forEach((hp) =>
    hotelMap.set(`${hp.person_id}_${hp.plan_date}`, hp),
  );
  const personNeedsHotel = (personId: string) =>
    projectDays.some((day) => {
      const dateStr = format(day, "yyyy-MM-dd");
      return hotelMap.get(`${personId}_${dateStr}`)?.needs_hotel;
    });
  const hotelPeople = activePeople.filter(
    (avail) => avail.person && personNeedsHotel(avail.person_id),
  );
  const personNameById = new Map(
    activePeople.map((avail) => [
      avail.person_id,
      `${avail.person?.first_name ?? ""} ${avail.person?.last_name ?? ""}`.trim(),
    ]),
  );
  const hotelRoomRows = new Map<
    string,
    {
      key: string;
      type: "Single" | "Double";
      people: string;
      notes: string;
      nights: Set<string>;
    }
  >();

  // Filter functions shown based on location filter
  const filteredFunctions =
    options.locationFilter === "all"
      ? functions
      : functions.filter(
          (series) =>
            (series.location_type ??
              series.project_function_group?.location_type) ===
            options.locationFilter,
        );

  const getPersonFunctionsByDate = (personId: string) => {
    const assignmentsByDate = new Map<
      string,
      { name: string; remark: string | null; cameraNumber: string | null }[]
    >();
    const addAssignment = (
      date: string,
      assignment: {
        name: string;
        remark: string | null;
        cameraNumber: string | null;
      },
    ) => {
      const assignments = assignmentsByDate.get(date) ?? [];
      assignments.push(assignment);
      assignmentsByDate.set(date, assignments);
    };

    filteredFunctions.forEach((series) => {
      series.days.forEach((day) => {
        day.assignments?.forEach((a) => {
          if (a.person_id === personId) {
            addAssignment(day.function_date, {
              name: series.name,
              remark: a.remark,
              cameraNumber: cameraNumberByAssignmentId[a.id] ?? null,
            });
          }
        });
      });
    });
    travelDays.forEach((travelDay) => {
      if (travelDay.person_id === personId) {
        addAssignment(travelDay.travel_date, {
          name: "Travel",
          remark: travelDay.remark,
          cameraNumber: null,
        });
      }
    });
    return assignmentsByDate;
  };

  const formatCameraAssignmentTag = (cameraNumber: string) =>
    cameraNumber.startsWith("CAM") ? cameraNumber : `CAM${cameraNumber}`;

  const parseCameraSortValue = (cameraNumber: string | null) => {
    if (!cameraNumber) return Number.POSITIVE_INFINITY;
    const match = cameraNumber.match(/\d+/);
    return match ? Number.parseInt(match[0], 10) : Number.POSITIVE_INFINITY;
  };

  const compareCameraNumbers = (a: string | null, b: string | null) => {
    if (a === b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return (
      parseCameraSortValue(a) - parseCameraSortValue(b) ||
      a.localeCompare(b, undefined, { numeric: true })
    );
  };

  const compareGroups = (
    a: ProjectFunctionGroup | null,
    b: ProjectFunctionGroup | null,
  ) => {
    if (a?.id === b?.id) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a.display_order - b.display_order || a.name.localeCompare(b.name);
  };

  const getMainFunctionForPerson = (
    personId: string,
    eventDayValues: string[],
  ) => {
    const assigned = filteredFunctions.flatMap((series) =>
      series.days.flatMap((day) =>
        (day.assignments ?? [])
          .filter((assignment) => assignment.person_id === personId)
          .map((assignment) => ({
            date: day.function_date,
            series,
            assignment,
            cameraNumber: cameraNumberByAssignmentId[assignment.id] ?? null,
          })),
      ),
    );

    for (const eventDay of eventDayValues) {
      const firstEventAssignment = assigned
        .filter((assignment) => assignment.date === eventDay)
        .sort((a, b) => a.series.name.localeCompare(b.series.name))[0];
      if (firstEventAssignment) return firstEventAssignment;
    }

    return assigned.sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        a.series.name.localeCompare(b.series.name),
    )[0];
  };

  // Get allergy label for a person: combines allergy tags and allergy notes
  const getPersonAllergyLabel = (avail: ProjectAvailability): string | null => {
    const tagNames =
      avail.person?.person_allergy_tags?.map((pat) => pat.allergy_tag.name) ??
      [];
    const allergyNotes = avail.person?.allergies_notes?.trim();

    const parts: string[] = [];
    if (tagNames.length > 0) {
      parts.push(tagNames.join(" + "));
    }
    if (allergyNotes) {
      parts.push(allergyNotes);
    }
    return parts.length > 0 ? parts.join(" — ") : null;
  };

  // Compute daily totals and allergy breakdown
  const dayTotals = projectDays.map((day) => {
    const dateStr = format(day, "yyyy-MM-dd");
    let lunch = 0,
      dinner = 0,
      singleRooms = 0;
    const doubleRoomKeys = new Set<string>();
    const lunchAllergies: Record<string, number> = {};
    const dinnerAllergies: Record<string, number> = {};
    let lunchNoRestriction = 0;
    let dinnerNoRestriction = 0;

    activePeople.forEach((avail) => {
      const meal = mealMap.get(`${avail.person_id}_${dateStr}`);
      const hp = hotelMap.get(`${avail.person_id}_${dateStr}`);
      if (meal?.lunch) lunch++;
      if (meal?.dinner) dinner++;
      if (hp?.needs_hotel) {
        if (hp.room_type === "double" && hp.roommate_person_id) {
          doubleRoomKeys.add(
            [avail.person_id, hp.roommate_person_id].sort().join("_"),
          );
        } else {
          singleRooms++;
        }
      }

      const allergyLabel = getPersonAllergyLabel(avail);
      if (meal?.lunch) {
        if (allergyLabel) {
          lunchAllergies[allergyLabel] =
            (lunchAllergies[allergyLabel] || 0) + 1;
        } else {
          lunchNoRestriction++;
        }
      }
      if (meal?.dinner) {
        if (allergyLabel) {
          dinnerAllergies[allergyLabel] =
            (dinnerAllergies[allergyLabel] || 0) + 1;
        } else {
          dinnerNoRestriction++;
        }
      }
    });

    const formatBreakdown = (
      allergies: Record<string, number>,
      noRestriction: number,
    ) => {
      const entries = Object.entries(allergies).map(
        ([name, count]) => `${count}x ${name}`,
      );
      if (entries.length > 0 && noRestriction > 0) {
        entries.push(`${noRestriction}x keine Einschränkung`);
      }
      return entries;
    };

    return {
      lunch,
      dinner,
      singleRooms,
      doubleRooms: doubleRoomKeys.size,
      lunchBreakdown: formatBreakdown(lunchAllergies, lunchNoRestriction),
      dinnerBreakdown: formatBreakdown(dinnerAllergies, dinnerNoRestriction),
    };
  });

  projectDays.forEach((day) => {
    const dateStr = format(day, "yyyy-MM-dd");
    const handledDoubleKeys = new Set<string>();

    activePeople.forEach((avail) => {
      const hp = hotelMap.get(`${avail.person_id}_${dateStr}`);
      if (!hp?.needs_hotel) return;

      const personName =
        personNameById.get(avail.person_id) || "Unnamed person";
      if (hp.room_type !== "double") {
        const key = `single_${avail.person_id}`;
        const row =
          hotelRoomRows.get(key) ??
          ({
            key,
            type: "Single",
            people: personName,
            notes: "",
            nights: new Set<string>(),
          } as const);
        row.nights.add(dateStr);
        hotelRoomRows.set(key, row);
        return;
      }

      if (!hp.roommate_person_id) {
        const key = `double_open_${avail.person_id}`;
        const row =
          hotelRoomRows.get(key) ??
          ({
            key,
            type: "Double",
            people: personName,
            notes: "No roommate selected",
            nights: new Set<string>(),
          } as const);
        row.nights.add(dateStr);
        hotelRoomRows.set(key, row);
        return;
      }

      const roomKey = [avail.person_id, hp.roommate_person_id].sort().join("_");
      if (handledDoubleKeys.has(roomKey)) return;
      handledDoubleKeys.add(roomKey);

      const key = `double_${roomKey}`;
      const row =
        hotelRoomRows.get(key) ??
        ({
          key,
          type: "Double",
          people: [personName, personNameById.get(hp.roommate_person_id)]
            .filter(Boolean)
            .join(" / "),
          notes: "",
          nights: new Set<string>(),
        } as const);
      row.nights.add(dateStr);
      hotelRoomRows.set(key, row);
    });
  });

  const hotelRooms = [...hotelRoomRows.values()].sort((a, b) => {
    const typeOrder = a.type.localeCompare(b.type);
    if (typeOrder !== 0) return typeOrder;
    return a.people.localeCompare(b.people);
  });

  const hasAnyAllergyBreakdown = dayTotals.some(
    (d) => d.lunchBreakdown.length > 0 || d.dinnerBreakdown.length > 0,
  );

  const dateRange =
    project.start_date && project.end_date
      ? `${format(new Date(project.start_date), "dd.MM.yyyy")} – ${format(new Date(project.end_date), "dd.MM.yyyy")}`
      : "";
  const displayName = formatProjectName(project.name, project.event_start_date);
  const groupsById = new Map<string, ProjectFunctionGroup>();
  functionGroups.forEach((group) => groupsById.set(group.id, group));
  functions.forEach((series) => {
    if (series.project_function_group) {
      groupsById.set(
        series.project_function_group.id,
        series.project_function_group,
      );
    }
  });
  const eventDayValues = eventDays
    .map((day) => format(day, "yyyy-MM-dd"))
    .sort();
  const groupedActivePeople = activePeople
    .map((avail) => {
      const name =
        `${avail.person?.first_name ?? ""} ${avail.person?.last_name ?? ""}`.trim();
      const mainFunction = getMainFunctionForPerson(
        avail.person_id,
        eventDayValues,
      );
      return {
        availability: avail,
        primaryGroup: getMainFunctionGroupForPerson({
          personId: avail.person_id,
          functions: filteredFunctions,
          groupsById,
          eventDays: eventDayValues,
        }),
        mainFunctionName: mainFunction?.series.name ?? null,
        mainCameraNumber: mainFunction?.cameraNumber ?? null,
        name,
      };
    })
    .sort(
      (a, b) =>
        compareGroups(a.primaryGroup, b.primaryGroup) ||
        (a.mainFunctionName ?? "\uffff").localeCompare(
          b.mainFunctionName ?? "\uffff",
        ) ||
        compareCameraNumbers(a.mainCameraNumber, b.mainCameraNumber) ||
        a.name.localeCompare(b.name),
    );

  const functionDateSet = new Set<string>();
  filteredFunctions.forEach((series) => {
    series.days.forEach((day) => functionDateSet.add(day.function_date));
  });
  const functionDays = projectDays.filter((day) =>
    functionDateSet.has(format(day, "yyyy-MM-dd")),
  );
  const functionDayHeaders = functionDays.map((d) => format(d, "dd.MM"));
  const functionDayLabels = functionDays.map((d) => format(d, "EEE"));
  const dayHeaders = projectDays.map((d) => format(d, "dd.MM"));
  const dayLabels = projectDays.map((d) => format(d, "EEE"));
  const intercomUsers = [...intercomAssignments]
    .map((assignment) => {
      const fullName = assignment.person
        ? `${assignment.person.firstName} ${assignment.person.lastName}`.trim()
        : (assignment.displayName ?? "").trim();
      return {
        id: assignment.id,
        fullName: fullName || assignment.unityUsername || "Unnamed user",
        username: assignment.unityUsername ?? "",
      };
    })
    .sort((a, b) => a.fullName.localeCompare(b.fullName));
  const displayHostname = intercomHostname?.trim() || "Not configured";

  // Dynamic crew list columns based on options
  const hasCrewRemarks = activePeople.some((avail) =>
    Boolean(avail.crew_list_remark?.trim()),
  );
  const colName = { width: options.includeContactData ? "18%" : "26%" };
  const colRemark = { width: options.includeContactData ? "16%" : "20%" };
  const colPhone = { width: "13%" };
  const colEmail = { width: "19%" };

  return (
    <Document>
      {/* Page 1: Crew list */}
      <Page size="A4" orientation="landscape" style={s.page}>
        <View style={s.headerSection}>
          <Image style={s.logo} src={logoImage} />
          <View style={s.headerRight}>
            <Text style={s.title}>
              Crew List
              {options.locationFilter !== "all" &&
                ` (${options.locationFilter === "remote" ? "Remote" : "On-site"})`}
            </Text>
            <Text style={s.subtitle}>
              {displayName} {project.number ? `(${project.number})` : ""}
            </Text>
            <Text style={s.subtitle}>{dateRange}</Text>
          </View>
        </View>

        <Text style={s.sectionTitle}>Team ({activePeople.length} people)</Text>
        <View style={s.table}>
          <View style={s.tableHeader}>
            <Text style={[s.headerText, colName]}>Name</Text>
            {options.includeContactData && (
              <>
                <Text style={[s.headerText, colPhone]}>Phone</Text>
                <Text style={[s.headerText, colEmail]}>Email</Text>
              </>
            )}
            {hasCrewRemarks && (
              <Text style={[s.headerText, colRemark]}>Remark</Text>
            )}
            {functionDays.map((day, idx) => (
              <View key={day.toISOString()} style={s.matrixCell}>
                <Text style={[s.headerText, { textAlign: "center" }]}>
                  {functionDayLabels[idx]}
                </Text>
                <Text style={[{ fontSize: 6, textAlign: "center" }]}>
                  {functionDayHeaders[idx]}
                </Text>
              </View>
            ))}
          </View>
          {groupedActivePeople.map((row, i) => {
            const person = row.availability.person;
            if (!person) return null;
            const previousGroup = groupedActivePeople[i - 1]?.primaryGroup;
            const showGroupHeader =
              i === 0 || previousGroup?.id !== row.primaryGroup?.id;
            const personFunctionsByDate = getPersonFunctionsByDate(
              row.availability.person_id,
            );
            return (
              <React.Fragment key={row.availability.id}>
                {showGroupHeader && (
                  <View style={s.groupHeaderRow}>
                    <Text style={s.groupHeaderText}>
                      {row.primaryGroup?.name ?? "Ungrouped"}
                    </Text>
                  </View>
                )}
                <View style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}>
                  <Text style={[s.cellText, colName]}>
                    {person.first_name} {person.last_name}
                  </Text>
                  {options.includeContactData && (
                    <>
                      <Text style={[s.cellTextSmall, colPhone]}>
                        {person.phone || ""}
                      </Text>
                      <Text style={[s.cellTextSmall, colEmail]}>
                        {person.email || ""}
                      </Text>
                    </>
                  )}
                  {hasCrewRemarks && (
                    <Text style={[s.cellTextSmall, colRemark]}>
                      {row.availability.crew_list_remark || ""}
                    </Text>
                  )}
                  {functionDays.map((day) => {
                    const dateStr = format(day, "yyyy-MM-dd");
                    const assignments =
                      personFunctionsByDate.get(dateStr) ?? [];
                    return (
                      <View key={day.toISOString()} style={s.matrixCell}>
                        {assignments.map((assignment, assignmentIndex) => (
                          <View
                            key={`${assignment.name}-${assignmentIndex}`}
                            wrap={false}
                          >
                            <Text style={s.matrixText}>
                              {assignment.name}
                              {assignment.cameraNumber
                                ? ` ${formatCameraAssignmentTag(assignment.cameraNumber)}`
                                : ""}
                            </Text>
                            {assignment.remark && (
                              <Text style={s.matrixRemarkText}>
                                ({assignment.remark})
                              </Text>
                            )}
                          </View>
                        ))}
                      </View>
                    );
                  })}
                </View>
              </React.Fragment>
            );
          })}
        </View>

        {project.crew_list_remarks && (
          <View style={s.remarksBlock}>
            <Text style={s.remarksLabel}>Remarks</Text>
            <Text style={s.remarksText}>{project.crew_list_remarks}</Text>
          </View>
        )}

        <View style={s.footer} fixed>
          <Text style={s.footerText}>{displayName} – Crew List</Text>
          <Text style={s.footerText}>
            Generated {format(new Date(), "dd.MM.yyyy HH:mm")}
          </Text>
        </View>
      </Page>

      {/* Page 2: Meal Planning */}
      {projectDays.length > 0 && activePeople.length > 0 && (
        <Page size="A4" orientation="landscape" style={s.page}>
          <View style={s.headerSection}>
            <Image style={s.logo} src={logoImage} />
            <View style={s.headerRight}>
              <Text style={s.title}>Meal Planning</Text>
              <Text style={s.subtitle}>{displayName}</Text>
            </View>
          </View>

          {/* Meal Planning - Summary only */}
          <Text style={s.sectionTitle}>Meals</Text>
          <View style={s.table}>
            <View style={s.tableHeader}>
              <Text style={[s.headerText, s.colPlanName]}></Text>
              {projectDays.map((day, idx) => (
                <View key={day.toISOString()} style={[s.colDay]}>
                  <Text style={[s.headerText, { textAlign: "center" }]}>
                    {dayLabels[idx]}
                  </Text>
                  <Text style={[{ fontSize: 6, textAlign: "center" }]}>
                    {dayHeaders[idx]}
                  </Text>
                </View>
              ))}
            </View>
            {/* Lunch totals */}
            <View style={s.tableRow}>
              <Text style={[s.cellText, s.colPlanName, { fontWeight: 600 }]}>
                Lunch
              </Text>
              {projectDays.map((day, idx) => (
                <Text
                  key={day.toISOString()}
                  style={[s.headerText, s.colDay, { textAlign: "center" }]}
                >
                  {dayTotals[idx].lunch}
                </Text>
              ))}
            </View>
            {/* Lunch allergy breakdown */}
            {hasAnyAllergyBreakdown &&
              dayTotals.some((d) => d.lunchBreakdown.length > 0) && (
                <View style={s.tableRowAlt}>
                  <Text style={[s.cellTextSmall, s.colPlanName]}>davon</Text>
                  {projectDays.map((day, idx) => (
                    <View key={day.toISOString()} style={[s.colDay]}>
                      {dayTotals[idx].lunchBreakdown.map((entry, j) => (
                        <Text
                          key={j}
                          style={[
                            s.allergySummaryText,
                            { textAlign: "center", fontSize: 5.5 },
                          ]}
                        >
                          {entry}
                        </Text>
                      ))}
                    </View>
                  ))}
                </View>
              )}
            {/* Dinner totals */}
            <View style={s.tableRow}>
              <Text style={[s.cellText, s.colPlanName, { fontWeight: 600 }]}>
                Dinner
              </Text>
              {projectDays.map((day, idx) => (
                <Text
                  key={day.toISOString()}
                  style={[s.headerText, s.colDay, { textAlign: "center" }]}
                >
                  {dayTotals[idx].dinner}
                </Text>
              ))}
            </View>
            {/* Dinner allergy breakdown */}
            {hasAnyAllergyBreakdown &&
              dayTotals.some((d) => d.dinnerBreakdown.length > 0) && (
                <View style={s.tableRowAlt}>
                  <Text style={[s.cellTextSmall, s.colPlanName]}>davon</Text>
                  {projectDays.map((day, idx) => (
                    <View key={day.toISOString()} style={[s.colDay]}>
                      {dayTotals[idx].dinnerBreakdown.map((entry, j) => (
                        <Text
                          key={j}
                          style={[
                            s.allergySummaryText,
                            { textAlign: "center", fontSize: 5.5 },
                          ]}
                        >
                          {entry}
                        </Text>
                      ))}
                    </View>
                  ))}
                </View>
              )}
          </View>

          {project.meals_remarks && (
            <View style={s.remarksBlock}>
              <Text style={s.remarksLabel}>Meals Remarks</Text>
              <Text style={s.remarksText}>{project.meals_remarks}</Text>
            </View>
          )}

          <View style={s.footer} fixed>
            <Text style={s.footerText}>{displayName} – Meal Planning</Text>
            <Text style={s.footerText}>
              Generated {format(new Date(), "dd.MM.yyyy HH:mm")}
            </Text>
          </View>
        </Page>
      )}

      {/* Page 3: Hotel Planning */}
      {projectDays.length > 0 &&
        (hotelPeople.length > 0 || project.hotels_remarks) && (
          <Page size="A4" orientation="landscape" style={s.page}>
            <View style={s.headerSection}>
              <Image style={s.logo} src={logoImage} />
              <View style={s.headerRight}>
                <Text style={s.title}>Hotel Planning</Text>
                <Text style={s.subtitle}>{displayName}</Text>
              </View>
            </View>

            {/* Hotel Planning */}
            <Text style={s.sectionTitle}>Hotel</Text>
            <View style={s.table}>
              <View style={s.tableHeader}>
                <Text style={[s.headerText, s.roomType]}>Room Type</Text>
                <Text style={[s.headerText, s.roomPeople]}>People</Text>
                <Text style={[s.headerText, s.roomNotes]}>Notes</Text>
                {projectDays.map((day, idx) => (
                  <View key={day.toISOString()} style={[s.roomDay]}>
                    <Text style={[s.headerText, { textAlign: "center" }]}>
                      {dayLabels[idx]}
                    </Text>
                    <Text style={[{ fontSize: 6, textAlign: "center" }]}>
                      {dayHeaders[idx]}
                    </Text>
                  </View>
                ))}
              </View>
              {hotelRooms.map((room, roomIdx) => (
                <View
                  key={room.key}
                  style={roomIdx % 2 === 0 ? s.tableRow : s.tableRowAlt}
                >
                  <Text style={[s.cellText, s.roomType]}>{room.type}</Text>
                  <Text style={[s.cellText, s.roomPeople]}>{room.people}</Text>
                  <Text style={[s.cellTextSmall, s.roomNotes]}>
                    {room.notes}
                  </Text>
                  {projectDays.map((day) => {
                    const dateStr = format(day, "yyyy-MM-dd");
                    return (
                      <Text
                        key={day.toISOString()}
                        style={[
                          s.headerText,
                          s.roomDay,
                          { textAlign: "center" },
                        ]}
                      >
                        {room.nights.has(dateStr) ? "X" : ""}
                      </Text>
                    );
                  })}
                </View>
              ))}
              {/* Totals */}
              <View style={s.summaryRow}>
                <Text style={[s.headerText, s.roomSummaryLabel]}>
                  Single Rooms
                </Text>
                {projectDays.map((day, idx) => (
                  <Text
                    key={day.toISOString()}
                    style={[s.headerText, s.roomDay, { textAlign: "center" }]}
                  >
                    {dayTotals[idx].singleRooms}
                  </Text>
                ))}
              </View>
              <View style={s.summaryRow}>
                <Text style={[s.headerText, s.roomSummaryLabel]}>
                  Double Rooms
                </Text>
                {projectDays.map((day, idx) => (
                  <Text
                    key={day.toISOString()}
                    style={[s.headerText, s.roomDay, { textAlign: "center" }]}
                  >
                    {dayTotals[idx].doubleRooms}
                  </Text>
                ))}
              </View>
            </View>

            {project.hotels_remarks && (
              <View style={s.remarksBlock}>
                <Text style={s.remarksLabel}>Hotel Remarks</Text>
                <Text style={s.remarksText}>{project.hotels_remarks}</Text>
              </View>
            )}

            <View style={s.footer} fixed>
              <Text style={s.footerText}>{displayName} – Hotel Planning</Text>
              <Text style={s.footerText}>
                Generated {format(new Date(), "dd.MM.yyyy HH:mm")}
              </Text>
            </View>
          </Page>
        )}

      {options.includeIntercomData && (
        <Page size="A4" orientation="portrait" style={s.page}>
          <View style={s.headerSection}>
            <Image style={s.logo} src={logoImage} />
            <View style={s.headerRight}>
              <Text style={s.title}>Intercom Access</Text>
              <Text style={s.subtitle}>{displayName}</Text>
            </View>
          </View>

          <View style={s.intercomHero}>
            <Text style={s.intercomLabel}>Unity hostname</Text>
            <Text style={s.intercomHostname}>{displayHostname}</Text>
            <Text style={s.intercomManual}>
              On the login screen, select Direct Connection, tap Settings, enter
              this hostname in MAIN IP/HOST, and keep port 20101.
            </Text>
          </View>

          <Text style={s.sectionTitle}>Users ({intercomUsers.length})</Text>
          <View style={s.table}>
            <View style={s.tableHeader}>
              <Text style={[s.headerText, s.credentialName]}>Full name</Text>
              <Text style={[s.headerText, s.credentialUsername]}>Username</Text>
              <Text style={[s.headerText, s.credentialPassword]}>Password</Text>
            </View>
            {intercomUsers.map((user, i) => (
              <View
                key={user.id}
                style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}
              >
                <Text style={[s.cellText, s.credentialName]}>
                  {user.fullName}
                </Text>
                <Text style={[s.cellText, s.credentialUsername]}>
                  {user.username}
                </Text>
                <Text style={[s.cellText, s.credentialPassword]}>
                  livemotion
                </Text>
              </View>
            ))}
          </View>

          <View style={s.footer} fixed>
            <Text style={s.footerText}>{displayName} – Intercom Access</Text>
            <Text style={s.footerText}>
              Generated {format(new Date(), "dd.MM.yyyy HH:mm")}
            </Text>
          </View>
        </Page>
      )}
    </Document>
  );
};

export async function generateCrewListPDF(
  data: Omit<CrewListPDFData, "options"> & { options: CrewListPDFOptions },
): Promise<void> {
  const blob = await pdf(<CrewListDocument data={data} />).toBlob();

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const projectName = formatProjectName(
    data.project.name,
    data.project.event_start_date,
  ).replace(/\s+/g, "_");
  link.download = `Crew_List_${projectName}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
